import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  CarOutlined, 
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  CheckOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import axios from '../../../../../utils/axiosConfig';
import { useAuth } from '../../../../../contexts/AuthContext';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, Polyline, CircleMarker, Circle, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const TRIP_COLOR_PALETTE = ['#2563eb', '#16a34a', '#a855f7', '#f97316', '#8b5cf6', '#0ea5e9', '#f59e0b', '#14b8a6'];

function getTripColorByIndex(index) {
  return TRIP_COLOR_PALETTE[index % TRIP_COLOR_PALETTE.length];
}

// Haversine formula to calculate distance between two GPS points in miles
function getDistanceMiles(lat1, lon1, lat2, lon2) {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Generate breadcrumb points at exact distance intervals along a route
function generateBreadcrumbsAtDistanceIntervals(positions, spacingMiles) {
  if (!positions || positions.length < 2 || !spacingMiles) return [];
  
  const breadcrumbs = [];
  let accumulatedDistance = 0;
  let nextBreadcrumbDistance = spacingMiles;
  
  for (let i = 1; i < positions.length; i++) {
    const [lat1, lon1] = positions[i - 1];
    const [lat2, lon2] = positions[i];
    const segmentDistance = getDistanceMiles(lat1, lon1, lat2, lon2);
    
    // Check if we cross one or more breadcrumb thresholds in this segment
    while (accumulatedDistance + segmentDistance >= nextBreadcrumbDistance) {
      // Calculate how far along this segment the breadcrumb should be
      const distanceToNext = nextBreadcrumbDistance - accumulatedDistance;
      const ratio = distanceToNext / segmentDistance;
      
      // Interpolate the position
      const breadcrumbLat = lat1 + (lat2 - lat1) * ratio;
      const breadcrumbLon = lon1 + (lon2 - lon1) * ratio;
      
      breadcrumbs.push([breadcrumbLat, breadcrumbLon]);
      nextBreadcrumbDistance += spacingMiles;
    }
    
    accumulatedDistance += segmentDistance;
  }
  
  return breadcrumbs;
}

const SelectRoutes = ({ campaignData, onUpdate, onNext, isAdminCreating, targetUserId }) => {
  const { token } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const [deviceCount, setDeviceCount] = useState(0);
  const routesPerPage = 6;
  const fetchAbortRef = useRef(null);

  const authHeader = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // Fetch Geotab databases on mount
  const fetchGeotabDatabases = useCallback(async () => {
    try {
      console.log('SelectRoutes - Fetching Geotab databases...');
      
      // If admin is creating for another user, fetch that user's Geotab accounts
      // Otherwise, fetch for the current logged-in user (no userId param needed)
      const endpoint = (isAdminCreating === true && targetUserId) 
        ? `/api/integrations/geotab/accounts?userId=${targetUserId}`
        : '/api/integrations/geotab/accounts';
      
      console.log('SelectRoutes - Fetching from endpoint:', endpoint);
      const { data } = await axios.get(endpoint, { headers: authHeader() });
      console.log('SelectRoutes - Received data:', data);
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      console.log('SelectRoutes - Items:', items);
      const databases = items
        .map(a => a?.database)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i);
      
      console.log('SelectRoutes - Found databases:', databases);
      if (databases.length > 0) {
        console.log('SelectRoutes - Setting geotabDatabases to:', databases);
        setGeotabDatabases(databases);
      } else {
        console.log('SelectRoutes - No databases found');
        setGeotabDatabases([]);
        setLoading(false);
      }
    } catch (e) {
      console.error('SelectRoutes - Error fetching Geotab accounts:', e);
      setGeotabDatabases([]);
      setLoading(false);
    }
  }, [authHeader, isAdminCreating, targetUserId]);

  // Fetch trips from Geotab API
  const fetchTrips = useCallback(async () => {
    if (geotabDatabases.length === 0) {
      setRoutes([]);
      setLoading(false);
      return;
    }

    // Use provided date range or default to last 7 days
    let startDate, endDate;
    if (campaignData.dateRange && campaignData.dateRange.length === 2 && campaignData.dateRange[0] && campaignData.dateRange[1]) {
      startDate = campaignData.dateRange[0].startOf('day');
      endDate = campaignData.dateRange[1].endOf('day');
    } else {
      // Default to last 7 days if no date range set
      startDate = dayjs().subtract(7, 'day').startOf('day');
      endDate = dayjs().endOf('day');
    }

    let abortController;
    try {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      abortController = new AbortController();
      fetchAbortRef.current = abortController;

      setCurrentPage(1);
      setLoading(true);

      console.log('=== SelectRoutes API Call ===');
      console.log('Date Range:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'));
      console.log('Databases:', geotabDatabases);

      // First, fetch all devices for the databases
      console.log('Fetching devices...');
      const devicesQuery = new URLSearchParams();
      devicesQuery.set('databases', geotabDatabases.join(','));
      console.log('Devices API URL:', `/api/integrations/geotab/devices?${devicesQuery.toString()}`);
      
      const devicesRes = await axios.get(`/api/integrations/geotab/devices?${devicesQuery.toString()}`, { 
        headers: authHeader(),
        signal: abortController.signal,
      });
      
      console.log('Devices response received:', devicesRes.data);
      let devices = Array.isArray(devicesRes.data?.devices) ? devicesRes.data.devices : [];
      
      // Filter devices to only include those from the requested databases
      devices = devices.filter(d => geotabDatabases.includes(d.database));
      
      console.log('Total devices from API:', devicesRes.data?.devices?.length || 0);
      console.log('Filtered devices for user databases:', devices.length);
      console.log('User databases:', geotabDatabases);
      
      setDeviceCount(devices.length);
      
      if (devices.length === 0) {
        console.log('No devices found for user databases');
        setRoutes([]);
        setLoading(false);
        message.info('No vehicles found in your Geotab account for the selected databases');
        return;
      }
      
      // Create device map for names
      const deviceMap = {};
      devices.forEach(d => { deviceMap[d.id] = d.name || d.id; });
      
      // Get all device IDs
      const deviceIds = devices.map(d => d.id);
      
      console.log('=== FETCHING TRIPS ===');
      console.log('Devices:', deviceIds.length);
      console.log('Databases:', geotabDatabases);
      console.log('Date range:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'));
      
      setLoadingTrips(true);
      
      // Use POST instead of GET to avoid URL length limits with many devices
      const requestBody = {
        deviceIds: deviceIds.join(','),
        databases: geotabDatabases.join(','),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
      
      console.log('Request body:', requestBody);
      
      const tripsRes = await axios.post('/api/integrations/geotab/trips', requestBody, { 
        headers: authHeader(),
        signal: abortController.signal,
      });
      
      console.log('=== TRIPS API RESPONSE ===');
      console.log('Full response:', tripsRes.data);
      console.log('Response type:', typeof tripsRes.data);
      console.log('Is array?', Array.isArray(tripsRes.data));
      console.log('Has trips property?', tripsRes.data?.trips);
      
      const trips = Array.isArray(tripsRes.data?.trips) ? tripsRes.data.trips : [];
      
      console.log('API Response - Number of trips:', trips.length);
      console.log('First trip sample:', trips[0]);
      console.log('First trip route:', trips[0]?.route);

      // Transform trips to routes
      const transformedRoutes = trips.map((trip, index) => {
        const distanceKm = trip.distance || 0;
        const distanceMiles = distanceKm / 1.60934;
        const durationMinutes = trip.durationMinutes || 0;
        
        const coordinates = Array.isArray(trip.route) 
          ? trip.route
              .filter(p => typeof p.latitude === 'number' && typeof p.longitude === 'number')
              .map(p => [p.latitude, p.longitude])
          : [];
        
        const formatAddress = (addr) => {
          if (!addr) return 'Unknown Location';
          if (typeof addr === 'string') return addr;
          if (typeof addr === 'object') {
            const parts = [];
            if (addr.address) parts.push(addr.address);
            if (addr.city) parts.push(addr.city);
            if (addr.state) parts.push(addr.state);
            return parts.length > 0 ? parts.join(', ') : 'Unknown Location';
          }
          return 'Unknown Location';
        };
        
        return {
          id: trip.id || `trip-${index}`,
          vehicleName: deviceMap[trip.deviceId || trip.device] || trip.device || 'Unknown Vehicle',
          vehicleColor: getTripColorByIndex(index),
          date: dayjs(trip.start).format('MMM D'),
          startTime: dayjs(trip.start).format('h:mm A'),
          endTime: dayjs(trip.stop).format('h:mm A'),
          startAddress: formatAddress(trip.startAddress),
          endAddress: formatAddress(trip.endAddress),
          distance: Math.round(distanceMiles),
          duration: Math.round(durationMinutes),
          coordinates: coordinates,
        };
      });

      console.log('=== ROUTE TRANSFORMATION ===');
      console.log('Transformed routes:', transformedRoutes.length);
      console.log('First transformed route:', transformedRoutes[0]);
      
      // Debug: Check coordinates
      const routesWithCoords = transformedRoutes.filter(r => r.coordinates && r.coordinates.length > 0);
      const routesWithoutCoords = transformedRoutes.filter(r => !r.coordinates || r.coordinates.length === 0);
      console.log(`Routes with coordinates: ${routesWithCoords.length}, without: ${routesWithoutCoords.length}`);
      if (routesWithoutCoords.length > 0) {
        console.log('Routes missing coordinates:', routesWithoutCoords);
      }
      
      // Check if routes have all required fields
      transformedRoutes.forEach((route, idx) => {
        if (idx < 3) { // Log first 3 routes
          console.log(`Route ${idx}:`, {
            id: route.id,
            vehicleName: route.vehicleName,
            hasCoordinates: route.coordinates?.length > 0,
            coordinateCount: route.coordinates?.length,
            startAddress: route.startAddress,
            endAddress: route.endAddress,
            distance: route.distance,
            duration: route.duration
          });
        }
      });
      
      console.log('Setting routes state with', transformedRoutes.length, 'routes');
      console.log('Routes to set:', transformedRoutes);
      setRoutes(transformedRoutes);
      setCurrentPage(1);
      console.log('Routes state set, clearing loading states');
      setLoading(false);
      setLoadingTrips(false);
      console.log('Loading states cleared - loading:', false, 'loadingTrips:', false);
      
      if (transformedRoutes.length > 0) {
        message.success(`Loaded ${transformedRoutes.length} route(s) from Geotab`);
      } else {
        message.info('No routes found for the selected date range. Try selecting a different date range.');
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
        console.log('Fetch aborted');
        return;
      }
      console.error('=== ERROR FETCHING ROUTES ===');
      console.error('Error:', err);
      console.error('Error name:', err?.name);
      console.error('Error message:', err?.message);
      console.error('Error response:', err?.response?.data);
      console.error('Error stack:', err?.stack);
      message.error('Failed to load routes: ' + (err.response?.data?.error || err.message));
      setRoutes([]);
    } finally {
      // Always clear loading states
      console.log('Clearing loading states');
      setLoading(false);
      setLoadingTrips(false);
    }
  }, [campaignData.dateRange, geotabDatabases, authHeader]);

  useEffect(() => {
    fetchGeotabDatabases();
  }, [fetchGeotabDatabases]);

  useEffect(() => {
    console.log('=== geotabDatabases changed ===');
    console.log('geotabDatabases:', geotabDatabases);
    console.log('geotabDatabases.length:', geotabDatabases.length);
    
    if (geotabDatabases.length > 0) {
      console.log('Calling fetchTrips...');
      fetchTrips();
    } else if (geotabDatabases.length === 0) {
      console.log('No databases, clearing loading state');
      setLoading(false);
      setRoutes([]);
    }
  }, [fetchTrips, geotabDatabases]);

  // Lazy geocode addresses for visible routes only
  useEffect(() => {
    if (routes.length === 0) return;

    const startIdx = (currentPage - 1) * routesPerPage;
    const visible = routes.slice(startIdx, startIdx + routesPerPage);

    // Check if any visible route still has coordinate-style addresses (e.g. "35.955,-86.813")
    const coordPattern = /^-?\d+\.\d+,-?\d+\.\d+$/;
    const needsGeocode = [];
    const coordSet = new Set();

    for (const route of visible) {
      if (route.startAddress && coordPattern.test(route.startAddress.trim())) {
        const [lat, lng] = route.startAddress.split(',').map(Number);
        const key = `${lat},${lng}`;
        if (!coordSet.has(key)) { coordSet.add(key); needsGeocode.push({ lat, lng }); }
      }
      if (route.endAddress && coordPattern.test(route.endAddress.trim())) {
        const [lat, lng] = route.endAddress.split(',').map(Number);
        const key = `${lat},${lng}`;
        if (!coordSet.has(key)) { coordSet.add(key); needsGeocode.push({ lat, lng }); }
      }
    }

    if (needsGeocode.length === 0) return;

    let cancelled = false;
    const doGeocode = async () => {
      try {
        const res = await axios.post('/api/integrations/geotab/geocode-batch', 
          { coordinates: needsGeocode },
          { headers: authHeader() }
        );
        if (cancelled) return;
        const results = res.data?.results || [];
        const addrMap = new Map();
        results.forEach(r => {
          if (r.address) addrMap.set(`${r.lat},${r.lng}`, r.address);
        });

        if (addrMap.size > 0) {
          setRoutes(prev => prev.map(route => {
            let updated = false;
            let newStart = route.startAddress;
            let newEnd = route.endAddress;
            if (newStart && coordPattern.test(newStart.trim()) && addrMap.has(newStart.trim())) {
              newStart = addrMap.get(newStart.trim());
              updated = true;
            }
            if (newEnd && coordPattern.test(newEnd.trim()) && addrMap.has(newEnd.trim())) {
              newEnd = addrMap.get(newEnd.trim());
              updated = true;
            }
            return updated ? { ...route, startAddress: newStart, endAddress: newEnd } : route;
          }));
        }
      } catch (e) {
        console.error('Lazy geocode failed:', e);
      }
    };

    doGeocode();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes.length, currentPage, authHeader]);

  // Debug: Track loading state changes
  useEffect(() => {
    console.log('Loading state changed:', loading);
  }, [loading]);

  useEffect(() => {
    console.log('Routes state changed:', routes.length, 'routes');
  }, [routes]);

  // Show loading state FIRST - before any calculations
  if (loading) {
    console.log('=== SHOWING LOADING SPINNER ===');
    return (
      <div className="step-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
          <p style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Loading routes from Geotab...</p>
          <p style={{ color: 'var(--ink-200)', fontSize: 14 }}>Fetching trip data and route coordinates</p>
        </div>
      </div>
    );
  }

  console.log('=== RENDER CYCLE ===');
  console.log('Routes state:', routes.length, 'routes');
  console.log('Loading state:', loading);
  console.log('Current page:', currentPage);
  
  const totalPages = Math.ceil(routes.length / routesPerPage);
  const startIndex = (currentPage - 1) * routesPerPage;
  const visibleRoutes = routes.slice(startIndex, startIndex + routesPerPage);
  
  console.log('Visible routes for page', currentPage, ':', visibleRoutes.length);
  console.log('Total pages:', totalPages);

  const selectedRoutes = campaignData.selectedRoutes || [];
  const targetingRadius = campaignData.targetingRadius || 0.5;
  const breadcrumbSpacing = campaignData.breadcrumbSpacing || null;

  const toggleRoute = (routeId) => {
    const isSelected = selectedRoutes.includes(routeId);
    if (isSelected) {
      const newSelectedRoutes = selectedRoutes.filter(id => id !== routeId);
      const selectedRoutesData = routes.filter(r => newSelectedRoutes.includes(r.id));
      onUpdate({ 
        selectedRoutes: newSelectedRoutes,
        selectedRoutesData: selectedRoutesData
      });
    } else {
      const newSelectedRoutes = [...selectedRoutes, routeId];
      const selectedRoutesData = routes.filter(r => newSelectedRoutes.includes(r.id));
      onUpdate({ 
        selectedRoutes: newSelectedRoutes,
        selectedRoutesData: selectedRoutesData
      });
    }
  };

  const selectAll = () => {
    const allIds = routes.map(r => r.id);
    onUpdate({ 
      selectedRoutes: allIds,
      selectedRoutesData: routes
    });
  };

  const unselectAll = () => {
    onUpdate({ 
      selectedRoutes: [],
      selectedRoutesData: []
    });
  };

  const handleContinue = () => {
    if (selectedRoutes.length > 0) {
      // Ensure selectedRoutesData is up to date before continuing
      const selectedRoutesData = routes.filter(r => selectedRoutes.includes(r.id));
      onUpdate({ selectedRoutesData: selectedRoutesData });
      onNext();
    }
  };

  // Calculate totals
  const selectedRoutesData = routes.filter(r => selectedRoutes.includes(r.id));
  const totalDistance = selectedRoutesData.reduce((sum, r) => sum + r.distance, 0);
  const totalBreadcrumbs = breadcrumbSpacing 
    ? selectedRoutesData.reduce((sum, r) => sum + Math.floor(r.distance / breadcrumbSpacing), 0)
    : 0;

  // Map center - Nashville area
  const mapCenter = [36.1627, -86.7816];

  return (
    <div className="step-container">
      <h1 className="step-title">Select Routes</h1>
      <p className="step-subtitle">Choose the routes to include in your campaign</p>

      <div className="routes-container">
        {/* Header */}
        <div className="stops-header">
          <div className="stops-header-left">
            <button className="select-all-btn" onClick={selectAll} disabled={routes.length === 0}>
              Select All
            </button>
            <button className="select-all-btn" onClick={unselectAll} disabled={selectedRoutes.length === 0} style={{ marginLeft: '8px' }}>
              Unselect All
            </button>
          </div>
          <div className="stops-header-right">
            {selectedRoutes.length} routes • {totalDistance} mi{breadcrumbSpacing ? ` • ~${totalBreadcrumbs} breadcrumbs` : ''}
            {deviceCount > 0 && (
              <span style={{ marginLeft: 12, color: 'var(--ink-200)', fontSize: 13 }}>
                • {deviceCount} vehicles queried
              </span>
            )}
          </div>
        </div>

        {/* Loading state for trips within the page */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
            <p style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 500 }}>Loading routes from Geotab...</p>
            <p style={{ marginTop: 8, color: 'var(--ink-200)', fontSize: 14 }}>Fetching trip data and route coordinates</p>
          </div>
        )}

        {/* Routes Grid - show when not loading */}
        {!loading && routes.length > 0 && (
          <div className="routes-grid">
          {(() => {
            console.log('=== RENDERING ROUTES GRID ===');
            console.log('About to render', visibleRoutes.length, 'visible routes');
            return visibleRoutes.map((route) => {
              const isSelected = selectedRoutes.includes(route.id);
              console.log('Rendering route:', route.id, route.vehicleName);
              return (
              <div
                key={route.id}
                className={`route-card ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleRoute(route.id)}
              >
                <div className="route-card-header">
                  <span 
                    className="stop-color-dot" 
                    style={{ backgroundColor: route.vehicleColor }}
                  />
                  <CarOutlined style={{ color: 'var(--ink-200)', fontSize: 14 }} />
                  <span className="stop-vehicle-name">{route.vehicleName}</span>
                  {isSelected && (
                    <div className="route-check">
                      <CheckOutlined style={{ color: '#fff', fontSize: 11 }} />
                    </div>
                  )}
                </div>

                <div className="route-time-info">
                  <ClockCircleOutlined style={{ color: '#9ca3af', fontSize: 13 }} />
                  <span>{route.date}, {route.startTime}</span>
                  <span className="route-time-to">to {route.endTime}</span>
                </div>

                <div className="route-addresses">
                  <div className="route-address-row">
                    <span className="route-address-icon start">✓</span>
                    <span>{route.startAddress}</span>
                  </div>
                  <div className="route-address-row">
                    <span className="route-address-icon end">●</span>
                    <span>{route.endAddress}</span>
                  </div>
                </div>

                <div className="route-card-footer">
                  <span className="route-stat">{Math.round(route.distance)} mi</span>
                  <span className="route-stat">{Math.round(route.duration)} min</span>
                </div>
              </div>
            );
            });
          })()}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination" style={{ marginTop: 24 }}>
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <LeftOutlined />
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <RightOutlined />
            </button>
          </div>
        )}

        {/* Map */}
        {routes.length > 0 && (
          <div style={{ marginTop: 24, maxWidth: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>
                {selectedRoutes.length > 0 ? `Selected Routes & Breadcrumbs (${selectedRoutes.length})` : 'Select routes above to display on map'}
                {loadingTrips && <LoadingOutlined style={{ marginLeft: 12, fontSize: 16, color: 'var(--ink)' }} spin />}
              </h3>
              
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Breadcrumb Spacing Selector */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: 'var(--ink-200)', marginRight: 4 }}>Drop every:</span>
                  {[0.5, 1, 2, 3, 4, 5].map((spacing) => (
                    <button
                      key={spacing}
                      onClick={() => onUpdate({ breadcrumbSpacing: spacing })}
                      style={{
                        padding: '5px 10px',
                        background: breadcrumbSpacing === spacing ? 'var(--ink)' : '#fff',
                        color: breadcrumbSpacing === spacing ? '#fff' : 'var(--ink-100)',
                        border: breadcrumbSpacing === spacing ? 'none' : '1px solid var(--paper-300)',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {spacing} mi
                    </button>
                  ))}
                </div>
                
                {/* Radius Selector Buttons */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: 'var(--ink-200)', marginRight: 4 }}>Radius:</span>
                  {[0.5, 1, 2, 3].map((radius) => (
                    <button
                      key={radius}
                      onClick={() => onUpdate({ targetingRadius: radius })}
                      style={{
                        padding: '5px 10px',
                        background: targetingRadius === radius ? 'var(--ink)' : '#fff',
                        color: targetingRadius === radius ? '#fff' : 'var(--ink-100)',
                        border: targetingRadius === radius ? 'none' : '1px solid var(--paper-300)',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {radius} mi
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ 
              height: '500px', 
              width: '100%',
              maxWidth: '100%',
              borderRadius: '24px', 
              overflow: 'hidden',
              boxShadow: 'var(--shadow-card)',
              border: '1px solid var(--paper-300)',
              position: 'relative',
              zIndex: 1
            }}>
              <MapContainer
                center={mapCenter}
                zoom={11}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
            <TileLayer
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
                {/* Only show selected routes on the map */}
                {routes.filter(r => selectedRoutes.includes(r.id)).map((route) => {
                  const positions = route.coordinates;
                  if (!positions || positions.length === 0) {
                    console.log('Route has no coordinates:', route.id, route);
                    return null;
                  }
                  
                  const startPoint = positions[0];
                  const endPoint = positions[positions.length - 1];
                  
                  // Calculate radius for bubbles
                  const radiusMeters = targetingRadius * 1609.34; // Convert miles to meters
                  
                  // Generate breadcrumb points at exact distance intervals
                  const breadcrumbPoints = breadcrumbSpacing 
                    ? generateBreadcrumbsAtDistanceIntervals(positions, breadcrumbSpacing)
                    : [];
                  
                  if (breadcrumbPoints.length > 0) {
                    console.log(`Route ${route.id}: distance=${route.distance}mi, spacing=${breadcrumbSpacing}mi, breadcrumbs=${breadcrumbPoints.length}`);
                  }
                  
                  return (
                    <React.Fragment key={route.id}>
                      {/* Route line */}
                      <Polyline
                        positions={positions}
                        color={route.vehicleColor}
                        weight={8}
                        opacity={0.9}
                      />
                      
                      {/* Breadcrumb points with radius bubbles */}
                      {breadcrumbPoints.map((point, idx) => (
                        <React.Fragment key={`breadcrumb-${route.id}-${idx}`}>
                          <Circle
                            center={point}
                            radius={radiusMeters}
                            pathOptions={{
                              color: route.vehicleColor,
                              fillColor: route.vehicleColor,
                              fillOpacity: 0.12,
                              weight: 1.5,
                              opacity: 0.4
                            }}
                          />
                          <CircleMarker
                            center={point}
                            radius={8}
                            pathOptions={{
                              color: '#fff',
                              fillColor: route.vehicleColor,
                              fillOpacity: 1,
                              weight: 2
                            }}
                          >
                            <Tooltip permanent={false} direction="top">
                              <div style={{ textAlign: 'center', minWidth: '120px' }}>
                                <div style={{ fontSize: '14px', marginBottom: '4px' }}>📍 Breadcrumb {idx + 1}</div>
                                <span style={{ fontSize: '12px', color: '#666' }}>{route.vehicleName}</span><br/>
                                <span style={{ fontSize: '11px', color: '#888' }}>{targetingRadius} mi radius</span>
                              </div>
                            </Tooltip>
                          </CircleMarker>
                        </React.Fragment>
                      ))}
                      
                      {/* Start marker - Pin style */}
                      {startPoint && (
                        <CircleMarker
                          center={startPoint}
                          radius={12}
                          pathOptions={{ 
                            color: '#fff', 
                            fillColor: route.vehicleColor, 
                            fillOpacity: 1,
                            weight: 4
                          }}
                        >
                          <Tooltip permanent={false} direction="top">
                            <div style={{ textAlign: 'left', minWidth: '200px' }}>
                              <div style={{ fontSize: '18px', marginBottom: '6px', textAlign: 'center' }}>📍 Trip Start</div>
                              <strong style={{ color: route.vehicleColor, fontSize: '14px' }}>{route.vehicleName}</strong><br/>
                              <span style={{ fontSize: '12px', color: '#666' }}>{route.date} • {route.startTime}</span><br/>
                              <span style={{ fontSize: '13px', marginTop: '4px', display: 'block' }}>{route.startAddress}</span>
                              <span style={{ color: 'var(--sage)', fontWeight: 600, marginTop: '6px', display: 'block' }}>✓ Selected Route</span>
                            </div>
                          </Tooltip>
                        </CircleMarker>
                      )}
                      
                      {/* End marker - Flag style */}
                      {endPoint && (
                        <CircleMarker
                          center={endPoint}
                          radius={12}
                          pathOptions={{ 
                            color: '#fff', 
                            fillColor: route.vehicleColor, 
                            fillOpacity: 1,
                            weight: 4
                          }}
                        >
                          <Tooltip permanent={false} direction="top">
                            <div style={{ textAlign: 'left', minWidth: '200px' }}>
                              <div style={{ fontSize: '18px', marginBottom: '6px', textAlign: 'center' }}>🏁 Trip End</div>
                              <strong style={{ color: route.vehicleColor, fontSize: '14px' }}>{route.vehicleName}</strong><br/>
                              <span style={{ fontSize: '12px', color: '#666' }}>{route.date} • {route.endTime}</span><br/>
                              <span style={{ fontSize: '13px', marginTop: '4px', display: 'block' }}>{route.endAddress}</span><br/>
                              <div style={{ marginTop: '6px', padding: '4px 8px', background: '#f0f0f0', borderRadius: '4px', display: 'inline-block' }}>
                                <strong>Distance:</strong> {Math.round(route.distance)} mi • <strong>Duration:</strong> {Math.round(route.duration)} min
                              </div>
                              <span style={{ color: 'var(--sage)', fontWeight: 600, marginTop: '6px', display: 'block' }}>✓ Selected Route</span>
                            </div>
                          </Tooltip>
                        </CircleMarker>
                      )}
                    </React.Fragment>
                  );
                })}
              </MapContainer>
            </div>
            {selectedRoutes.length === 0 && (
              <p style={{ marginTop: 12, color: '#8c8c8c', fontSize: 14, textAlign: 'center' }}>
                Select routes above to highlight them on the map
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          <strong>{selectedRoutes.length}</strong> routes • {totalDistance} mi{breadcrumbSpacing ? ` • ~${totalBreadcrumbs} breadcrumbs` : ''}
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
          disabled={selectedRoutes.length === 0}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default SelectRoutes;
